import { ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { cn } from '@/utils/cn';
import { KIND_LABELS } from './constants';
import { DiffStat } from './DiffStat';
import type { DiffSectionKind, DiffStats } from './types';

interface SectionShellProps {
  id: string;
  label: string;
  kind: DiffSectionKind;
  stats: DiffStats;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const SectionShell = ({
  id,
  label,
  kind,
  stats,
  collapsed,
  onToggle,
  children,
}: SectionShellProps) => {
  const contentId = useId();
  const empty = stats.add + stats.del + stats.mod === 0;
  return (
    <section
      data-diff-section={id}
      className="border border-neutral-200 rounded overflow-hidden bg-white"
    >
      <div className="h-9 px-3 flex items-center gap-2 bg-neutral-25 border-b border-neutral-150">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={contentId}
          aria-label={`${collapsed ? 'Rozbalit' : 'Sbalit'} sekci ${label}`}
          data-testid={`compare-section-toggle-${id}`}
          className="h-5 w-5 -ml-1 inline-flex items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <ChevronRight
            size={12}
            className={cn('transition-transform', !collapsed && 'rotate-90')}
          />
        </button>
        <span className="text-[12.5px] font-semibold text-neutral-900 truncate">{label}</span>
        {empty ? (
          <span className="text-xs text-neutral-500">Beze změn</span>
        ) : (
          <DiffStat
            {...stats}
            unit={kind === 'text' ? 'řádky textu' : 'položky'}
            className="ml-1"
          />
        )}
        <span className="ml-auto text-[11px] text-neutral-400">{KIND_LABELS[kind]}</span>
      </div>
      <div id={contentId} hidden={collapsed}>
        {!collapsed && children}
      </div>
    </section>
  );
};
